/**
 * Calendar-day maths in the viewer's own timezone.
 *
 * Counting days by subtracting millisecond timestamps flips at the wrong
 * hour (UTC midnight, or whenever "now" crosses 24h from the target) and is
 * off by one across daylight-saving changes. Everything here compares local
 * calendar days instead.
 */

/** A bare 'YYYY-MM-DD' means that calendar day in local time; anything else
 *  is parsed as a timestamp. Null when missing or unparseable. */
export function parseLocalDay(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 'YYYY-MM-DD' of the viewer's local calendar day (not the UTC day). */
export function localDayKey(date: Date = new Date()): string {
  const y = String(date.getFullYear()).padStart(4, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Whole local calendar days from `a` to `b` (positive when b is later). */
function localDayDiff(a: Date, b: Date): number {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / 86_400_000);
}

/** Days from today (local) until `target`: 0 = today, negative = in the past.
 *  Null when `target` is missing or invalid. */
export function daysUntil(
  target: string | number | Date | null | undefined,
  now: Date = new Date()
): number | null {
  const t = parseLocalDay(target);
  return t ? localDayDiff(now, t) : null;
}

/** Days since `target` (local): 0 = today, negative = in the future. */
export function daysSince(
  target: string | number | Date | null | undefined,
  now: Date = new Date()
): number | null {
  const n = daysUntil(target, now);
  return n === null ? null : -n;
}
